import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { articles, articleHref, type Article } from '../lib/content-loader';
import { findSection } from '../lib/sections';
import './SearchModal.css';

type Props = {
  open: boolean;
  onClose: () => void;
};

type SearchResult = Article & { score: number };

// Build a searchable index once at module load — articles are static at build time.
const searchIndex = articles.map(a => ({
  article: a,
  haystack: (a.title + ' ' + a.summary + ' ' + a.body).toLowerCase(),
  titleLower: a.title.toLowerCase(),
  summaryLower: a.summary.toLowerCase(),
}));

function search(query: string): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const scored = searchIndex
    .map(({ article, haystack, titleLower, summaryLower }) => {
      let score = 0;
      for (const tok of tokens) {
        if (titleLower.includes(tok)) score += 10;
        if (summaryLower.includes(tok)) score += 4;
        if (haystack.includes(tok)) score += 1;
      }
      return { ...article, score };
    })
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  return scored;
}

export function SearchModal({ open, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const results = useMemo(() => search(query), [query]);

  // Reset on open + focus input.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Defer focus until after layout — modal is display:flex from a class.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Clamp activeIndex when results change.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, Math.max(results.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[activeIndex];
      if (target) {
        navigate(articleHref(target));
        onClose();
      }
    }
  }

  if (!open) return null;

  return (
    <div
      className="ds-search-scrim open"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ds-search-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Search documentation"
      >
        <div className="ds-search-input-row">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search across all docs…"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd>esc</kbd>
        </div>
        <div className="ds-search-results">
          {!query.trim() ? (
            <div className="ds-search-empty">
              Type to search across {articles.length}{' '}
              {articles.length === 1 ? 'article' : 'articles'}.
            </div>
          ) : results.length === 0 ? (
            <div className="ds-search-empty">No matches for "{query}".</div>
          ) : (
            results.map((r, idx) => {
              const section = findSection(r.section);
              return (
                <a
                  key={r.slug}
                  href={articleHref(r)}
                  className={`ds-search-result${idx === activeIndex ? ' active' : ''}`}
                  onClick={e => {
                    e.preventDefault();
                    navigate(articleHref(r));
                    onClose();
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <div className="ds-search-result-section">
                    {section?.title ?? r.section}
                  </div>
                  <div className="ds-search-result-title">
                    <Highlight text={r.title} query={query} />
                  </div>
                  {r.summary && (
                    <div className="ds-search-result-summary">
                      <Highlight text={r.summary} query={query} />
                    </div>
                  )}
                </a>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function Highlight({ text, query }: { text: string; query: string }) {
  const tokens = query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (tokens.length === 0) return <>{text}</>;
  const re = new RegExp(`(${tokens.join('|')})`, 'ig');
  const parts = text.split(re);
  return (
    <>
      {parts.map((part, i) =>
        re.test(part) ? (
          <mark key={i}>{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
